import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import "reflect-metadata";
import {validateJSON, validateJSONNow} from "../web/validation";

/** Validator for a object cost rate model type. */
export const ObjectCostRateModelValidator = t.intersection([
  t.type({
    id: t.string,
    objectId: t.string,
    rateModelId: t.string,
    objectType: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    rateId: t.string,
    rateModelName: t.string,
    rateModelType: t.string,
    rateModelCompanyType: t.string,
    invoiceAccountLookupId: t.string,
    taxInvoiceAccountLookupId: t.string,
    billableHours: t.number,
    billableQty: t.number,
    billableFreightType: t.number,
    timeRate: t.number,
    freightRate: t.number,
    timeCost: t.number,
    freightCost: t.number,
    loadCost: t.number,
    loadRate: t.number,
    taxCost: t.number,
    taxRate: t.number,
    taxableAmount: t.number,
    totalCost: t.number,
    personCost: t.number,
    trailerCost: t.number,
    siteRestrictionTimeCost: t.number,
    siteRestrictionFreightCost: t.number,
    siteRestrictionLoadCost: t.number,
    siteRestrictionCost: t.number,
    baseFuelSurchargeLoadCost: t.number,
    percentFuelSurchargeLoadCost: t.number,
    fuelSurchargeCost: t.number,
    shiftLoadCost: t.number,
    ticketRejectLoadCost: t.number,
    pickupDemurrageCost: t.number,
    deliveryDemurrageCost: t.number,
    demurrageCost: t.number,
    splitLoadCost: t.number,
    genericFreightCost: t.number,
    genericLoadCost: t.number,
    genericTimeCost: t.number,
    wellId: t.string,
    wellName: t.string,
    wellAfe: t.string,
    wellAllocation: t.number,
  }),
]);

/** Validator for an array of object cost rate model types. */
export const ObjectCostRateModelArrayValidator = t.array(
  ObjectCostRateModelValidator
);

export type ObjectCostRateModel = t.TypeOf<typeof ObjectCostRateModelValidator>;
export type ObjectCostRateModelArray = t.TypeOf<
  typeof ObjectCostRateModelArrayValidator
>;

/**
 * An object cost rate model association
 */
@Exclude()
export class ObjectCostRateModelEntity {
  @Expose() id!: string;
  @Expose() objectId!: string;
  @Expose() rateModelId!: string;
  @Expose() rateModelName?: string;
  @Expose() rateModelType?: string;
  @Expose() rateModelCompanyType?: string;
  @Expose() objectType!: string;
  @Expose() rateId?: string;
  @Expose() invoiceAccountLookupId?: string;
  @Expose() taxInvoiceAccountLookupId?: string;
  @Expose() billableHours?: number;
  @Expose() billableQty?: number;
  @Expose() billableFreightQty?: number;
  @Expose() timeRate?: number;
  @Expose() freightRate?: number;
  @Expose() timeCost?: number;
  @Expose() freightCost?: number;
  @Expose() loadCost?: number;
  @Expose() loadRate?: number;
  @Expose() taxCost?: number;
  @Expose() taxRate?: number;
  @Expose() taxableAmount?: number;
  @Expose() totalCost?: number;
  @Expose() personCost?: number;
  @Expose() trailerCost?: number;
  @Expose() siteRestrictionTimeCost?: number;
  @Expose() siteRestrictionFreightCost?: number;
  @Expose() siteRestrictionLoadCost?: number;
  @Expose() siteRestrictionCost?: number
  @Expose() baseFuelSurchargeLoadCost?: number;
  @Expose() percentFuelSurchargeLoadCost?: number;
  @Expose() fuelSurchargeCost?: number
  @Expose() shiftLoadCost?: number;
  @Expose() ticketRejectLoadCost?: number;
  @Expose() pickupDemurrageCost?: number
  @Expose() deliveryDemurrageCost?: number
  @Expose() demurrageCost?: number
  @Expose() splitLoadCost?: number
  @Expose() genericFreightCost?: number
  @Expose() genericLoadCost?: number
  @Expose() genericTimeCost?: number
  @Expose() wellId?: string
  @Expose() wellName?: string
  @Expose() wellAfe?: string
  @Expose() wellAllocation?: number
  @Expose() createDttm!: number;
  @Expose() lastModifiedDttm!: number;

  /**
   * Constructor for ObjectCostRateModelEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: ObjectCostRateModel): ObjectCostRateModelEntity {
    return plainToInstance(ObjectCostRateModelEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<ObjectCostRateModel> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ObjectCostRateModelValidator, object);
  }

  asTypeValue(): ObjectCostRateModel {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ObjectCostRateModelValidator, object);
  }
}
